// Format a date to UK format (dd/mm/yyyy)
export const formatDate = (dateInput) => {
  if (!dateInput) return '';
  
  let date;
  if (typeof dateInput === 'string' && dateInput.includes('/')) {
    // Already in UK format
    return dateInput;
  }
  
  try {
    if (typeof dateInput === 'object' && dateInput.seconds) {
      // Handle Firestore Timestamp
      date = new Date(dateInput.seconds * 1000);
    } else {
      date = new Date(dateInput);
    }

    if (isNaN(date.getTime())) return '';

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
};

// Parse a date string to UK format
export const parseDate = (dateStr) => {
  if (!dateStr) return '';

  try {
    // If in ISO format (YYYY-MM-DD)
    if (dateStr.includes('-')) {
      const [year, month, day] = dateStr.split('-');
      return `${day}/${month}/${year}`;
    }

    // If already in UK format (DD/MM/YYYY)
    if (dateStr.includes('/')) {
      const [day, month, year] = dateStr.split('/');
      if (day && month && year) {
        return dateStr;
      }
    }

    // For any other format, convert through Date object
    const date = new Date(dateStr);
    if (!isNaN(date.getTime())) {
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    }

    return '';
  } catch (error) {
    console.error('Error parsing date:', error);
    return '';
  }
};

// Convert UK format date to ISO format for storage
export const toISODate = (ukDate) => {
  if (!ukDate) return '';
  
  try {
    if (ukDate.includes('/')) {
      const [day, month, year] = ukDate.split('/');
      return `${year}-${month}-${day}`;
    }
    return ukDate;
  } catch (error) {
    console.error('Error converting to ISO date:', error);
    return '';
  }
};

// Format date for HTML date input (yyyy-mm-dd)
export const formatDateForInput = (dateStr) => {
  if (!dateStr) return '';
  
  try {
    if (dateStr.includes('/')) {
      // Convert from UK format (DD/MM/YYYY) to ISO format (YYYY-MM-DD)
      const [day, month, year] = dateStr.split('/');
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    
    // If already in ISO format
    if (dateStr.includes('-')) {
      return dateStr;
    }

    // For any other format, convert through Date object
    const date = new Date(dateStr);
    if (!isNaN(date.getTime())) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }

    return '';
  } catch (error) {
    console.error('Error formatting date for input:', error);
    return '';
  }
};

// Compare two dates in UK format
export const compareDates = (date1, date2) => {
  if (!date1 || !date2) return 0;
  
  try {
    const [day1, month1, year1] = date1.split('/');
    const [day2, month2, year2] = date2.split('/');
    
    const d1 = new Date(year1, month1 - 1, day1);
    const d2 = new Date(year2, month2 - 1, day2);
    
    return d1 - d2;
  } catch (error) {
    console.error('Error comparing dates:', error);
    return 0;
  }
};

// Get days between two UK format dates
export const getDaysBetween = (date1, date2) => {
  if (!date1 || !date2) return 0;
  
  try {
    const [day1, month1, year1] = date1.split('/');
    const [day2, month2, year2] = date2.split('/');
    
    const d1 = new Date(year1, month1 - 1, day1);
    const d2 = new Date(year2, month2 - 1, day2);
    
    const diffTime = Math.abs(d2 - d1);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  } catch (error) {
    console.error('Error calculating days between dates:', error);
    return 0;
  }
};

// Check if a date is valid
export const isValidDate = (dateStr) => {
  if (!dateStr) return false;
  
  try {
    if (dateStr.includes('/')) {
      const [day, month, year] = dateStr.split('/');
      const date = new Date(year, month - 1, day);
      return date.getDate() === parseInt(day) &&
             date.getMonth() === parseInt(month) - 1 &&
             date.getFullYear() === parseInt(year);
    }
    return false;
  } catch (error) {
    console.error('Error validating date:', error);
    return false;
  }
};

// Get today's date in UK format
export const getTodayUK = () => {
  const today = new Date();
  const day = String(today.getDate()).padStart(2, '0');
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const year = today.getFullYear();
  return `${day}/${month}/${year}`;
};

// Check if a date is expiring soon (within 30 days)
export const isExpiringSoon = (dateStr) => {
  if (!dateStr || !isValidDate(dateStr)) return false;
  
  const today = getTodayUK();
  const daysUntilExpiry = getDaysBetween(today, dateStr);
  
  return daysUntilExpiry >= 0 && daysUntilExpiry <= 30;
};

// Check if a date is expired (before today)
export const isExpired = (dateStr) => {
  if (!dateStr || !isValidDate(dateStr)) return false;
  
  const today = getTodayUK();
  const daysUntilExpiry = getDaysBetween(today, dateStr);
  
  return daysUntilExpiry < 0;
};

/**
 * Parse certificate expiry date from various formats (Firestore Timestamp, Date, string, etc.)
 * Returns Date object or null if invalid
 */
export const parseCertificateExpiryDate = (dateInput) => {
  if (!dateInput) return null;
  try {
    if (typeof dateInput?.toDate === 'function') {
      return dateInput.toDate();
    }
    if (typeof dateInput === 'object' && typeof dateInput.seconds === 'number') {
      return new Date(dateInput.seconds * 1000);
    }
    if (dateInput instanceof Date) {
      return isNaN(dateInput.getTime()) ? null : dateInput;
    }
    if (typeof dateInput === 'string') {
      // Handle UK format DD/MM/YYYY
      if (dateInput.includes('/')) {
        const [day, month, year] = dateInput.split('/');
        const date = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
        return isNaN(date.getTime()) ? null : date;
      }
      // Handle ISO format YYYY-MM-DD
      const date = new Date(dateInput);
      return isNaN(date.getTime()) ? null : date;
    }
    return null;
  } catch {
    return null;
  }
};

/** Days threshold for "expiring soon" - matches Cloud Function and email alerts */
const EXPIRING_SOON_DAYS = 30;

/**
 * Check certificate expiry status. Works with any date format (Timestamp, Date, string).
 * Returns { isExpired, isExpiringSoon, daysUntil }
 */
export const checkCertificateExpiry = (dateInput) => {
  const expiryDate = parseCertificateExpiryDate(dateInput);
  if (!expiryDate) {
    return { isExpired: false, isExpiringSoon: false, daysUntil: null };
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const expiryAtMidnight = new Date(expiryDate);
  expiryAtMidnight.setHours(0, 0, 0, 0);
  const diffMs = expiryAtMidnight - today;
  const daysUntil = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  const isExpired = daysUntil < 0;
  const isExpiringSoon = !isExpired && daysUntil <= EXPIRING_SOON_DAYS;
  return { isExpired, isExpiringSoon, daysUntil };
};

export const getNextRentDueDate = (startDate, frequency = 'monthly', dueDay = 1) => {
  if (!startDate) return null;

  const today = new Date();
  let nextDue;

  // If startDate is a string, convert it to a Date object
  if (typeof startDate === 'string') {
    // Assuming startDate is in UK format (DD/MM/YYYY)
    const [day, month, year] = startDate.split('/');
    nextDue = new Date(year, month - 1, day);
  } else if (startDate.seconds) {
    // If it's a Firestore Timestamp
    nextDue = new Date(startDate.seconds * 1000);
  } else {
    nextDue = new Date(startDate);
  }

  // If nextDue is invalid, return null
  if (isNaN(nextDue.getTime())) return null;

  // Calculate next due date based on frequency
  while (nextDue <= today) {
    switch (frequency.toLowerCase()) {
      case 'weekly':
        nextDue.setDate(nextDue.getDate() + 7);
        break;
      case 'biweekly':
        nextDue.setDate(nextDue.getDate() + 14);
        break;
      case 'monthly':
        nextDue.setMonth(nextDue.getMonth() + 1);
        break;
      case 'quarterly':
        nextDue.setMonth(nextDue.getMonth() + 3);
        break;
      case 'annually':
        nextDue.setFullYear(nextDue.getFullYear() + 1);
        break;
      default:
        nextDue.setMonth(nextDue.getMonth() + 1);
    }
  }

  // Ensure the due day is consistent
  nextDue.setDate(dueDay);

  // Format the date in UK format
  const formattedNextDue = `${String(nextDue.getDate()).padStart(2, '0')}/${String(nextDue.getMonth() + 1).padStart(2, '0')}/${nextDue.getFullYear()}`;
  
  return formattedNextDue;
};

export const isSameMonth = (date1, date2) => {
  return date1.getFullYear() === date2.getFullYear() && 
         date1.getMonth() === date2.getMonth();
};
